/**
 * Workshop Domain Definition (domain-def@4) — recognition-card elicitation.
 *
 * Workshops (domain-model.md §3.6, ADR-013 lineage) elicit deeper self-knowledge
 * by RECOGNITION (forced choice over generated stimuli) and consolidate it into
 * typed context documents, gate-sequenced W1 → W2 → W3:
 *  - W1 `self_knowledge` → `CandidateContext` (+ enriches Profile / ledger)
 *  - W2 `search_profile`  → `SearchContext` (drives discovery)
 *  - W3 `profiler`        → `ProfilerRubric` (drives matching)
 *  - W4 `writing_style`   → `WritingStyleProfile` (the person's VOICE; §W4 below)
 *
 * W4 is NOT a fourth link in that chain. It produces no `ContextSnapshot` at all —
 * its product is a versioned ASSET the Kompass renders and letter/Kurzprofil
 * generation consumes in place of today's `"voice": null`. It hangs off W1 alone
 * (its stimuli must be grounded in the person's own values/trajectory), so it is
 * reachable as soon as the Werte-Sektion exists and does not queue behind W2/W3.
 *
 * Re-derivation deltas from the quarry:
 *  - The three produced context documents live in `career-primitives.ts` (so the
 *    typed `ContextSnapshot` union resolves for Application too — DOM-m4); this
 *    definition REFERENCES them, it does not re-author them.
 *  - **No `workshop.ClaimElicited`** (DOM-B1): when the fold elicits a claim, the
 *    ONE canonical write is `claim.Proposed(origin: elicited)` on the Evidence
 *    stream; `workshop.StepAdvanced` lists the produced claim URNs (canonical-first,
 *    §3.0). No claim body ever exists on the Workshop stream.
 *  - **New `workshop.SearchTargetingEdited`** — the durable home for the W2
 *    targeting editor (DOM-m2).
 *  - `workshop.ContextProduced` carries the typed raw `ContextSnapshot` AND the
 *    presented German narrative distinctly (llm-workflows §4.2).
 *
 * Stream: `Agg::career::{userId}::Workshop::{workshopId}`.
 */

import type { DomainDefinitionJSON } from "./schema"

const workshopDomain = {
  schemaVersion: "domain-def@4",
  name: "Workshop",
  version: "1.0.0",
  description:
    "Workshops W1/W2/W3 — gate-sequenced recognition-card elicitation producing CandidateContext, SearchContext, and the ProfilerRubric — plus W4 Schreibstil, a bespoke writing track producing the WritingStyleProfile asset",

  types: {
    /** W1 self-knowledge | W2 search-profile | W3 profiler | W4 writing-style.
     *  ADDITIVE (2026-07-29): `writing_style` is the »Schreibstil« track. It is a
     *  peer of the other three as a workshop KIND, not a fourth stage of the
     *  W1→W2→W3 consolidation chain — it produces an asset, never a context
     *  document, so every `ContextSnapshot`-shaped reader must exclude it
     *  EXPLICITLY rather than fall through to a wrong branch. */
    WorkshopType: "'self_knowledge' | 'search_profile' | 'profiler' | 'writing_style'",

    /** Per-step gate state. `in_progress` = mid-dialogue (a stimulus generated /
     *  a turn recorded, the step not yet accepted). */
    WorkshopStepGate: "'locked' | 'available' | 'in_progress' | 'accepted'",

    // ─── Recognition-card elicitation (ADR-013) ──────────────────

    /** The primitive a card instantiates — the cognitive operation it asks for.
     *  `open_narrative` is the OPTIONAL express lane (the old free-text question). */
    ElicitationPrimitiveKind:
      "'pairwise' | 'best_worst' | 'vignette' | 'simulation' | 'semantic_differential' | 'card_sort' | 'why_ladder' | 'nuance' | 'open_narrative'",

    /** One option row in a card (best_worst / vignette / simulation / pairwise side). */
    CardOption: {
      "+": "reject",
      id: "NonEmptyString",
      label: "NonEmptyString",
      "detail?": "string",
    },

    /** The two named poles of a semantic-differential axis. */
    SemanticDifferentialAxis: {
      "+": "reject",
      left: "NonEmptyString",
      right: "NonEmptyString",
    },

    /** A graded veto: `ungern` = soft de-prioritization, `niemals` = hard anti-value. */
    Veto: {
      "+": "reject",
      id: "NonEmptyString",
      intensity: "'ungern' | 'niemals'",
    },

    /** A rendered stimulus the user reacts to. Persisted by `workshop.StimulusGenerated`. */
    ElicitationCard: {
      "+": "reject",
      id: "NonEmptyString",
      primitive: "ElicitationPrimitiveKind",
      /** Eyebrow naming the mode (e.g. "Abwägung", "Situation"). */
      eyebrow: "string",
      /** The question itself — one clear sentence. */
      prompt: "NonEmptyString",
      "sublegend?": "string",
      /** vignette / simulation: the framed scenario the options respond to. */
      "scenario?": "string",
      /** best_worst / vignette / simulation / card_sort: the option rows. */
      "options?": "CardOption[]",
      /** card_sort: the fixed buckets the options are sorted into; omit for participant-named lanes. */
      "lanes?": "CardOption[]",
      /** pairwise: the two sides. */
      "optionA?": "CardOption",
      "optionB?": "CardOption",
      /** semantic_differential: the bipolar axis. */
      "axis?": "SemanticDifferentialAxis",
      /** why_ladder: the surface stance this probe climbs up from. */
      "seed?": "string",
      /** VISIBLE voice — a short, warm, personal read the candidate sees above the card
       *  (German »du«, 1–2 sentences). A TRUE ADDITION derived from the evidence so far;
       *  it must NOT restate the question or recap the answer. */
      "rationale?": "string",
      /** DISCLOSED voice — the "Überlegung" the candidate can expand on demand: plain full
       *  sentences saying WHY this card was asked. No method jargon, no internal ids, no
       *  confidence numbers. Distinct from `rationale` (never a paraphrase of it). Optional
       *  for backward-compatible replay of pre-two-voice events; a live probe always authors it. */
      "denkweise?": "string",
    },

    // ─── Typed answers (discriminated by `kind`) ─────────────────

    PairwiseAnswer: {
      "+": "reject",
      cardId: "NonEmptyString",
      kind: "'pairwise'",
      choice: "'A' | 'B'",
      "why?": "string",
    },
    BestWorstAnswer: {
      "+": "reject",
      cardId: "NonEmptyString",
      kind: "'best_worst'",
      best: "NonEmptyString",
      worst: "NonEmptyString",
      "why?": "string",
    },
    VignetteAnswer: {
      "+": "reject",
      cardId: "NonEmptyString",
      kind: "'vignette'",
      choice: "NonEmptyString",
      "vetoes?": "Veto[]",
      "why?": "string",
    },
    SimulationAnswer: {
      "+": "reject",
      cardId: "NonEmptyString",
      kind: "'simulation'",
      choice: "NonEmptyString",
      "vetoes?": "Veto[]",
      "why?": "string",
    },
    DifferentialAnswer: {
      "+": "reject",
      cardId: "NonEmptyString",
      kind: "'semantic_differential'",
      position: "UnitInterval",
      "why?": "string",
    },
    CardPlacement: {
      "+": "reject",
      cardId: "NonEmptyString",
      lane: "NonEmptyString",
    },
    CardSortAnswer: {
      "+": "reject",
      cardId: "NonEmptyString",
      kind: "'card_sort'",
      placements: "CardPlacement[]",
      "why?": "string",
    },
    /** nuance — a typed "nicht sicher?" outlet; reads as a SKIP, never a choice. */
    NuanceAnswer: {
      "+": "reject",
      cardId: "NonEmptyString",
      kind: "'nuance'",
      nuance: "'tie' | 'depends' | 'need_example' | 'wait' | 'rare'",
      "why?": "string",
    },
    /** open_narrative — the OPTIONAL express lane (intentional user prose). */
    OpenNarrativeAnswer: {
      "+": "reject",
      cardId: "NonEmptyString",
      kind: "'open_narrative'",
      text: "NonEmptyString",
    },

    /** The discriminated union of every card answer. */
    CardAnswer:
      "PairwiseAnswer | BestWorstAnswer | VignetteAnswer | SimulationAnswer | DifferentialAnswer | CardSortAnswer | NuanceAnswer | OpenNarrativeAnswer",

    /** One recorded turn: the answer a user confirmed for a card. */
    Turn: {
      "+": "reject",
      cardId: "NonEmptyString",
      answer: "CardAnswer",
      occurredAt: "Timestamp",
    },

    /**
     * The signal-fold digest — what a step's turns mean, computed server-side.
     * Ranked by `importance`, gated by `confidence`; `boundary` keeps synthesis
     * honest. Persisted by `workshop.InterpretationProposed`.
     */
    ElicitedEvidence: {
      "+": "reject",
      stepId: "NonEmptyString",
      primitive: "ElicitationPrimitiveKind",
      /** Candidate-facing German summary of what the signal says. */
      summary: "NonEmptyString",
      /** What this evidence does NOT establish. */
      boundary: "string",
      constructTargets: "string[]",
      importance: "UnitInterval",
      confidence: "UnitInterval",
      "tension?": "boolean",
      raw: "CardAnswer",
      /** Model-anchoring tags (§5a, V4) — the Schwartz/Big-Five dimensions this turn
       *  speaks to, derived in code from the pyramid cards the block probed. Additive
       *  and OPTIONAL: pre-interview elicited evidence (the pack runner) carries none;
       *  the readout composes over whatever tags are present (honest gap otherwise). */
      "tags?": "ValueModelTag[]",
      /** Which way the answer went, per id the card offered (`AnswerDirection`).
       *  Additive and OPTIONAL: evidence folded before the direction fix carries none,
       *  and the primitives with no derivable sign (open text, semantic differential,
       *  card sort, nuance) never carry one. Absent means "this dimension was TOUCHED,
       *  not which way it moved" — a reader must treat it as coverage, never support. */
      "directions?": "AnswerDirection[]",
    },

    /** Per-step AI-run telemetry — a compact snapshot of the captured RunStep's
     *  display fields; the Runs stream owns the authoritative `Run`. `engine` is
     *  the shared `RunEngine` primitive. null/absent for the deterministic seed. */
    WorkshopStepRun: {
      "+": "reject",
      model: "NonEmptyString",
      engine: "RunEngine",
      "promptTokens?": "number >= 0",
      "completionTokens?": "number >= 0",
      "cachedTokens?": "number >= 0",
      costUsd: "number >= 0",
      "latencyMs?": "number >= 0",
      /**
       * Provenance — WHICH captured run and WHICH call inside it produced this artifact.
       * ADDITIVE and OPTIONAL: the authoritative record stays the `Run` on the Runs stream
       * (this is still the compact display badge), but the badge is the only thing that
       * travels WITH the artifact, so it is the only place a later reader can pick the join
       * key up from. Without it a judgement on a re-rendered card could never be attributed
       * to a model or a cost: the mutation response is gone after the first render, and the
       * fold is what every subsequent read sees.
       *
       * Absent on every artifact captured before this field existed, and on any produced
       * without a captured run — such a signal is counted as UNATTRIBUTED in the rollup
       * rather than guessed at.
       */
      "runRef?": "Urn",
      /** 0-based index into that run's `steps[]` — the call this artifact came out of. */
      "stepIndex?": "number.integer >= 0",
    },

    WorkshopStep: {
      "+": "reject",
      id: "NonEmptyString",
      title: "NonEmptyString",
      gate: "WorkshopStepGate",
      /** open_narrative express-lane prompt/response (backward compat). */
      "prompt?": "string",
      "response?": "string",
      "primitive?": "ElicitationPrimitiveKind",
      "stimulus?": "ElicitationCard | null",
      "turns?": "Turn[]",
      "evidence?": "ElicitedEvidence[]",
      "run?": "WorkshopStepRun | null",
      /** True for the single skippable deep-probe (why_ladder) step. */
      "deepProbe?": "boolean",
      /** Step KIND discriminator (W1 redesign §6). The Werte-Kartenspiel is a step
       *  *kind*, not an ElicitationPrimitiveKind — it is a bespoke deterministic
       *  reduction machine, never a card_sort primitive. Defaults `elicitation` on
       *  replay of pre-redesign steps. `writing` (W4, additive 2026-07-29) marks the
       *  Schreibstil rounds for the same reason the Kartenspiel is its own kind: the
       *  user WRITES there, they do not recognise a stimulus, so no card primitive
       *  describes the step honestly. */
      "kind?": "'cardgame' | 'elicitation' | 'interview' | 'writing'",
      /** The committed value pyramid for a `cardgame` step (§3). null before commit. */
      "pyramid?": "ValuePyramid | null",
    },

    // ─── Werte-Kartenspiel (W1 redesign §3, V2) — deterministic reduction machine ──
    //
    // The card game is PERMANENTLY AI-FREE (Q7): zero inference, no hints, no model
    // call anywhere — the pyramid is the one artifact guaranteed free of AI influence.
    // The intermediate taps are ephemeral client-reducer state; only two things touch
    // the log — an OPTIONAL last-write-wins `ValueGameProgressed` snapshot (device-
    // portable resume) and the committing `ValuePyramidFormed`. Multiple pyramids over
    // time are valid history (supersede-by-latest, Q6); nothing hard-codes one-per-user.

    /** The phase of the deterministic reduction: pairwise reduce → optional refine
     *  (bounded rescue/swap) → pick 1 Lebenswert → pick 3 Superkräfte → confirm. */
    ValueGamePhase: "'reduce' | 'refine' | 'lebenswert' | 'superkraefte' | 'confirm'",

    /**
     * The full serializable game state — the client reducer's snapshot, carried by
     * the OPTIONAL `ValueGameProgressed` (LWW). Holds enough for a device-portable
     * mid-game resume: the session-frozen shuffle, the three piles (Sichern/Raus/
     * Eventuell retten = keep/out/maybe), the in-round cursor, the rescue budget, and
     * the pyramid picks. `cardId`s reference the frozen catalog (the comparability key).
     */
    ValueGameSnapshot: {
      "+": "reject",
      /** The session seed — the deterministic shuffle + budgets derive from it. */
      seed: "NonEmptyString",
      phase: "ValueGamePhase",
      /** The session-frozen shuffle of every catalog `cardId` (never re-shuffled). */
      order: "string[]",
      /** »Sichern« — the survivors carried into the current round (the working deck). */
      keep: "string[]",
      /** »Raus« — hard-eliminated cards. */
      out: "string[]",
      /** »Eventuell retten« — unsure losers, eligible for a rescue swap. */
      maybe: "string[]",
      /** Winners accumulated in the CURRENT reduction round (mid-round resume). */
      roundWinners: "string[]",
      /** Cursor into the current round's pair sequence (0-based). */
      pairIndex: "number.integer >= 0",
      /** Which reduction round (0-based) — informational. */
      round: "number.integer >= 0",
      /** Remaining rescue/swap budget for the refine stage. */
      rescueBudget: "number.integer >= 0",
      /** The chosen Lebenswert (once past the pyramid phases); null before. */
      "lebenswert?": "string | null",
      /** The chosen Superkräfte (up to 3). */
      superkraefte: "string[]",
      updatedAt: "Timestamp",
    },

    // ─── Runden-Planner (W1 redesign §4, V3) — the LLM round-plan seam ──────────────
    //
    // Phase 2 takes the LATEST pyramid + probe grounding and plans 2–3 rounds of
    // question blocks over the user's OWN chosen values, toward MODEL-COVERAGE
    // (Schwartz + Big-Five). The planner PLANS steps; it does not answer them — each
    // block executes through the existing probe/fold loop unchanged (§4). Round count
    // is a budget, not a constant (blocks-flexible ruling): the planner mixes
    // recognition (`pairwise`/`best_worst`/`vignette`/`semantic_differential`) with
    // open/recall (`why_ladder`/`open_narrative`) — it never extends the primitive set,
    // the block is a plan entry, not a new ElicitationPrimitiveKind. Captured by
    // `workshop.RoundPlanProduced` (auditable); validated by `RoundPlanDraft` (never cast).

    /** The probe VOICE axis a block elicits on (the interview subset of the construct
     *  vocabulary — the deterministic probe carries `values`/`motivations` voices). The
     *  model axis a block targets is carried separately by `framework`/`dimension`. */
    RoundPlanConstruct: "'values' | 'motivations'",

    /**
     * One planned interview block — a `WorkshopStep`-shaped entry (id, title, primitive,
     * seed, and the pyramid `cardId`s it probes). `primitive` reuses
     * `ElicitationPrimitiveKind` (blocks-flexible — no new primitive). `targetCardIds`
     * are the frozen pyramid cardIds the block puts in tension (the comparability key,
     * §9). `framework`/`dimension` name the model axis the block aims to cover so the
     * planner can plan toward coverage. `seed` is the static starting card the probe
     * reshapes (open blocks may omit it). `intent` is the planner's internal reasoning —
     * NOT candidate-facing (the probe authors the candidate voices).
     */
    RoundPlanBlock: {
      "+": "reject",
      id: "NonEmptyString",
      title: "NonEmptyString",
      primitive: "ElicitationPrimitiveKind",
      construct: "RoundPlanConstruct",
      targetCardIds: "string[]",
      framework: "ValueFramework",
      dimension: "NonEmptyString",
      "seed?": "ElicitationCard | null",
      "intent?": "string",
    },

    /** One planned round — a set of blocks toward model-coverage. Round count is a
     *  budget (§4); a candidate-facing German `title` gives the round a product voice.
     *  `goal` is a short candidate-facing German sentence naming what this round is FOR
     *  (shown on the plan rail so the user sees the known high-level path); optional and
     *  additive (older plans without it fold cleanly). */
    RoundPlanRound: {
      "+": "reject",
      round: "number.integer >= 0",
      title: "NonEmptyString",
      "goal?": "string",
      blocks: "RoundPlanBlock[]",
    },

    /** The Phase-2 artifact: 2–3 rounds of blocks planned over the user's OWN pyramid
     *  values, toward Schwartz + Big-Five coverage. `note` is the planner's internal
     *  coverage rationale; the server stamps `plannedAt`. */
    RoundPlan: {
      "+": "reject",
      rounds: "RoundPlanRound[]",
      note: "string",
      plannedAt: "Timestamp",
    },

    // ─── Schwartz circumplex prognosis (W1 redesign §5c, V4.2 — meaning calibration) ──
    //
    // The interview's REWARD artifact: a prognosed Schwartz value profile the
    // circumplex wheel renders. One sector per basic value (the ten, in the validated
    // circular order — `value-circumplex.ts`), each with a magnitude (how strongly the
    // person's answers + pyramid load on it) and an HONEST confidence (low when the
    // interview has not yet sharpened it). The magnitude/confidence MATH is composed in
    // code from the pyramid ranking + tagged interview evidence (the same score-math
    // discipline that omits `score` from `MatchSignalsDraft`); the model may refine the
    // numbers and authors the `insight`. Additive + supersede-by-latest: recomputed
    // after each round and finally at completion.

    /** One pyramid card behind a sector — the German value word the person actually
     *  picked, and the tier they put it in. */
    SchwartzBasisCard: {
      "+": "reject",
      name: "NonEmptyString",
      tier: "ValuePyramidTier",
    },

    /** One interview question behind a sector, and what it did to it. `effect` is
     *  candidate-facing German: »stützt« = the answer moved toward this value,
     *  »grenzt ab« = away from it, »berührt« = the question touched the value without
     *  saying which way (open text, a differential — coverage, not endorsement). */
    SchwartzBasisProbe: {
      "+": "reject",
      question: "NonEmptyString",
      effect: "'stützt' | 'grenzt ab' | 'berührt'",
    },

    /**
     * WHY a sector reads the way it does — code-composed, never authored. A person must
     * be able to see how their number arose: which cards they ranked, which answers moved
     * it and in which direction, and whether the model adjusted it. A wheel nobody can
     * interrogate is a wheel nobody can disagree with.
     */
    SchwartzSectorBasis: {
      "+": "reject",
      cards: "SchwartzBasisCard[]",
      probes: "SchwartzBasisProbe[]",
      /**
       * WHERE the radius comes from — the distinction this whole repair exists to make.
       * `ranking` = the length is your card game and nothing else; your answers did not
       * move it. `ranking+answers` = something you said pushed it, and `probes` says
       * which and which way. A person must be able to tell "your ranking says this"
       * from "your answers say this", because those invite very different disagreement.
       */
      magnitudeFrom: "'ranking' | 'ranking+answers'",
      /** The signed adjustment the interview applied to the ranking, in [-1, 1]. */
      interviewDelta: "-1 <= number <= 1",
      /** True when the model's prognosis nudged the code-composed numbers. */
      "modelAdjusted?": "boolean",
    },

    /** One sector of the circumplex — a basic Schwartz value with its prognosed
     *  magnitude (0..1) and honest confidence (0..1). `value`/`higherOrder` are the
     *  frozen catalog keys; `label` the German sector label (code fills all three from
     *  the circumplex vocabulary — the model authors only the numbers). `basis` is the
     *  code-composed explanation; OPTIONAL and additive, so profiles prognosed before it
     *  existed replay unchanged (a reader shows no explanation rather than a wrong one). */
    SchwartzSector: {
      "+": "reject",
      value: "NonEmptyString",
      label: "NonEmptyString",
      higherOrder: "NonEmptyString",
      magnitude: "UnitInterval",
      confidence: "UnitInterval",
      "basis?": "SchwartzSectorBasis",
    },

    /** The prognosed Schwartz value profile — ten sectors in circumplex order plus a
     *  short candidate-facing German `insight` (»Das ist dein Bild«). `insight` is the
     *  model's read of the shape; empty under the deterministic default. */
    SchwartzProfile: {
      "+": "reject",
      sectors: "SchwartzSector[]",
      insight: "string",
    },

    // ─── W4 · Schreibstil (writing_style) — the bespoke three-round voice track ────
    //
    // W4 asks the person to WRITE, three times, and reads their voice off what they
    // actually wrote. It is a bespoke track on the Workshop stream — own events, own
    // step `kind`, `foldWorkshop` ignoring what it does not know — exactly like the
    // Werte-Kartenspiel. It deliberately adds NO `ElicitationPrimitiveKind` member and
    // NO `CardAnswer` variant: those vocabularies are consumed by
    // `@platform/elicitation-core`, so a career product decision must not widen them.
    // Rounds ① and ② produce the person's OWN words; round ③ shows them a text written
    // back IN the derived style so they can correct it before the asset is committed.

    /** The three rounds of the writing track: ① the person writes prose from a bullet
     *  set, ② they judge invented scenarios written two ways, ③ they validate a text
     *  written in the style derived from ① and ②. Named (not numbered) so a `basis`
     *  entry stays readable when the round order is ever revisited. */
    WritingStyleRound: "'prose' | 'scenarios' | 'validation'",

    /** Round ① stimulus — the bullet set the user turns into free text. The bullets are
     *  grounded in the user's OWN material (profile / values / trajectory), so the sample
     *  they write is about something real; a sample about nothing carries no voice. */
    WritingBulletSet: {
      "+": "reject",
      /** Candidate-facing German framing: which APPLICATION passage to write (one sentence). */
      brief: "NonEmptyString",
      /** 3–4 short German first-person bullets — light enough that the person supplies
       *  only the FORM. Denser sets measure content-thinking rather than voice. */
      bullets: "NonEmptyString[]",
      /** WHERE the bullets came from, candidate-facing German — honesty about grounding. */
      grounding: "string",
    },

    /** One stylistic variant of the SAME content — the recognition pair of round ②.
     *  Holding the content fixed is what makes the choice a STYLE judgement: if the two
     *  texts said different things, the pick would only reveal what they agree with. */
    WritingVariant: {
      "+": "reject",
      id: "NonEmptyString",
      /** Short German label naming the stylistic hypothesis (»knapp und direkt«). */
      label: "NonEmptyString",
      text: "NonEmptyString",
    },

    /** Round ② stimulus — an INVENTED situation (1–2 German sentences) grounded in the
     *  user's prior workshop results, with two variants of the same content to choose
     *  between. Invented rather than recalled: asking someone to remember a real mail
     *  gets an anecdote, asking them to judge a concrete one gets a style verdict. */
    WritingScenario: {
      "+": "reject",
      id: "NonEmptyString",
      situation: "NonEmptyString",
      /** Why this scenario was invented for THIS person (the disclosed Überlegung voice —
       *  the same two-voice discipline as `ElicitationCard.denkweise`). */
      "grounding?": "string",
      variants: "WritingVariant[]",
    },

    /** The user's verdict on one scenario. `rewrite` is the highest-signal artifact in the
     *  whole workshop: their own words fixing a machine's guess at their voice. `fit` keeps
     *  the verdict honest — »fast« is not »trifft«, and a track that cannot record a near-miss
     *  will read every near-miss as agreement. */
    WritingScenarioVerdict: {
      "+": "reject",
      scenarioId: "NonEmptyString",
      chosenVariant: "NonEmptyString",
      fit: "'trifft' | 'fast' | 'daneben'",
      "rewrite?": "string",
    },

    /** One named axis of how this person writes, with an honest confidence and the
     *  code-composed record of WHICH rounds produced it (never model-authored basis).
     *  A trait a person cannot trace back to something they did is a trait they cannot
     *  disagree with — the same reason `SchwartzSectorBasis` exists. */
    WritingStyleTrait: {
      "+": "reject",
      /** Candidate-facing German axis name (»Satzlänge«, »Nähe«, »Konkretheit«). */
      axis: "NonEmptyString",
      /** Where this person sits on it — one German sentence, in their terms. */
      reading: "NonEmptyString",
      confidence: "UnitInterval",
      /** Which rounds the trait rests on — composed in code, not authored. */
      basis: "WritingStyleRound[]",
    },

    /** A verbatim piece of the person's OWN writing — the grounding a model may imitate.
     *  Never model-rewritten: the moment a sample is "cleaned up" it stops being evidence
     *  of a voice and becomes evidence of the cleaner's voice. `origin` says which round
     *  it came out of, so a reader can weigh a fixing rewrite above a first draft. */
    WritingSample: {
      "+": "reject",
      origin: "'prose' | 'scenario_rewrite' | 'validation_edit' | 'manual'",
      text: "NonEmptyString",
      /** What the text was written for (the brief / the scenario situation). */
      "context?": "string",
    },

    /**
     * The W4 ASSET (P-A) — a versioned, value-bearing writing-style profile. Consumable
     * beyond applications: the Kompass renders it as a document, generation consumes it.
     * Supersede-by-latest; `version` increments per confirmed derivation so a reader can
     * see it is a living artifact and not a one-shot. `guidance` and `avoid` are the two
     * halves of one instruction — an author model that is only told what to do will drift
     * back to house style on everything it was not told to avoid.
     */
    WritingStyleProfile: {
      "+": "reject",
      version: "number.integer >= 1",
      /** Candidate-facing German paragraph: how this person writes. »Das ist deine Stimme.« */
      summary: "NonEmptyString",
      traits: "WritingStyleTrait[]",
      /** Imperative German DO-instructions an author model can actually follow. */
      guidance: "NonEmptyString[]",
      /** What does NOT sound like this person — the negative half, equally load-bearing. */
      avoid: "string[]",
      samples: "WritingSample[]",
      /** Default address register observed in their own writing. */
      "register?": "AddressRegister",
      locale: "NonEmptyString",
      /** Honest overall confidence — LOW when only round ① ran. */
      confidence: "UnitInterval",
      derivedAt: "Timestamp",
    },

    Workshop: {
      "+": "reject",
      id: "Urn",
      owner: "UserId",
      type: "WorkshopType",
      steps: "WorkshopStep[]",
      cursor: "number >= 0",
      /** URN of the context document this workshop produced (once complete) */
      "producedContext?": "Urn | null",
      createdAt: "Timestamp",
      updatedAt: "Timestamp",
    },

    // ─── Event payloads ──────────────────────────────────────────

    /** A workshop instance was created for a workshop kind (prereq gate checked in the handler). */
    WorkshopStartedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      type: "WorkshopType",
    },

    /** A step's gate advanced; carries URN refs of claims produced by the step
     *  (written canonical-first per §3.0 — the revisit anchor). */
    WorkshopStepAdvancedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      stepId: "NonEmptyString",
      gate: "WorkshopStepGate",
      /** The user's response, captured when the step is accepted */
      "response?": "string",
      /** URNs of `claim.Proposed` claims this step produced on the Evidence stream. */
      "producedClaimRefs?": "Urn[]",
    },

    /** The user edited the produced SearchConfig targeting lists after consolidation (W2 editor). */
    WorkshopSearchTargetingEditedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      config: "SearchConfig",
    },

    /** Consolidation output — the typed raw ContextSnapshot + the presented German narrative. */
    WorkshopContextProducedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      contextUrn: "Urn",
      contextKind: "ContextKind",
      /** The RAW part — the typed context document (CandidateContext | SearchContext | ProfilerRubric). */
      raw: "ContextSnapshot",
      /** The PRESENTATION part — the freely-authored German narrative + provenance map + meta. */
      "narrative?": "PresentedText",
      "prov?": "ProvenanceBundle",
    },

    WorkshopCompletedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
    },

    /** The interviewer generated a recognition-card stimulus for a step (gate → in_progress),
     *  OR skipped it (`stepSufficient` — the uploaded artifacts already resolve the step, so no
     *  card is authored). The event description always allowed a skip; `card` is therefore
     *  OPTIONAL and a skip carries `stepSufficient: true` + the `rationale` (the "Überlegung")
     *  the interviewer would otherwise put on the card. (ADDITIVE amendment, slice 3 — see the
     *  evidence log DEFECT note: the original payload required `card`, which could not represent
     *  the `stepSufficient` skip the event description promised.) */
    WorkshopStimulusGeneratedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      stepId: "NonEmptyString",
      "card?": "ElicitationCard",
      /** True when the artifacts already resolve the step → skipped, no card authored. */
      "stepSufficient?": "boolean",
      /** The interviewer's candidate-facing Überlegung — carried at payload level for a skip
       *  (a non-skip carries it on `card.rationale`). */
      "rationale?": "string",
      "run?": "WorkshopStepRun | null",
    },

    /** The user confirmed an answer for a card (one turn). */
    WorkshopTurnRecordedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      stepId: "NonEmptyString",
      answer: "CardAnswer",
      occurredAt: "Timestamp",
    },

    /** The signal-fold produced the step's evidence digest (the boundary AI step). */
    WorkshopInterpretationProposedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      stepId: "NonEmptyString",
      evidence: "ElicitedEvidence[]",
    },

    // ─── Werte-Kartenspiel events (§3, V2) — zero-inference, owner-scoped ──────────

    /** OPTIONAL device-portable resume snapshot (last-write-wins). Not authoritative —
     *  the pyramid is the durable artifact; this only lets a resume continue mid-game. */
    WorkshopValueGameProgressedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      snapshot: "ValueGameSnapshot",
    },

    /** The COMMITTING event — the 8 chosen cards with their tiers (1·3·4). The durable
     *  Phase-1 artifact and the input to Phase 2. Multiple over time are valid history
     *  (supersede-by-latest, Q6). Carries NO run telemetry — the game is AI-free (Q7). */
    WorkshopValuePyramidFormedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      pyramid: "ValuePyramid",
    },

    /** The Runden-Planner produced a round plan (§4, V3) from the latest pyramid +
     *  grounding — auditable. `run` is the compact telemetry of the real AI call (absent
     *  under the deterministic floor); the authoritative Run stays on the Credits stream.
     *  Multiple plans over time are valid history (supersede-by-latest — a re-plan after a
     *  new pyramid is legitimate). */
    WorkshopRoundPlanProducedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      plan: "RoundPlan",
      "run?": "WorkshopStepRun | null",
    },

    /** The Runden-Planner REVISED the remaining (not-yet-completed) rounds after a round
     *  finished (§4, V3.1 — "revision after each completed epic"). Carries the FULL amended
     *  plan (completed rounds preserved verbatim, only rounds after `afterRound` may change),
     *  a short candidate-facing German `reason` for the change, and `afterRound` = the last
     *  round index that was already accepted when the revision ran. Supersede-by-latest with
     *  `RoundPlanProduced` (the fold reads whichever plan-bearing event is latest). `run` is
     *  the compact telemetry of the reasoning call (absent under the deterministic floor). */
    WorkshopRoundPlanRevisedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      plan: "RoundPlan",
      /** Candidate-facing German reason for the amendment (shown as »Plan angepasst«). */
      reason: "NonEmptyString",
      /** The last already-accepted round index at revision time (rounds ≤ this are frozen). */
      afterRound: "number.integer >= 0",
      "run?": "WorkshopStepRun | null",
    },

    /** The interview prognosed (or re-prognosed) a Schwartz circumplex profile (§5c,
     *  V4.2) from the pyramid ranking + all interview answers incl. free-texts.
     *  Additive + supersede-by-latest (the latest wins; recomputed after each round and
     *  at completion). `afterRound` = the round index that had just completed when it
     *  ran (absent = the final, whole-interview prognosis). `run` is the compact
     *  telemetry of the real AI call (absent under the deterministic default). */
    WorkshopSchwartzProfilePrognosedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      profile: "SchwartzProfile",
      "afterRound?": "number.integer >= 0",
      "run?": "WorkshopStepRun | null",
    },

    /** The culminating Phase-3 artifact (W1 redesign §5b–§7, V5) — the per-value
     *  detail + the four personal statements, authored WITH the user (HITL) and only
     *  emitted on the user's confirm/edit. `values` are the reconciled `PersonalValueEntry`
     *  (cardId/tier fixed from the committed pyramid — not the model); `statements`
     *  carry the code-composed per-framework readouts (Schwartz + Big-Five, never
     *  mixed). `run` is the compact telemetry of the synthesis AI call (absent under the
     *  deterministic floor); the authoritative Run stays on the Credits stream. Multiple
     *  over time are valid history (supersede-by-latest — re-runnable, Q6). */
    WorkshopWerteSektionProducedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      values: "PersonalValueEntry[]",
      statements: "PersonalStatements",
      "run?": "WorkshopStepRun | null",
    },

    // ─── W4 · Schreibstil events (writing_style) — additive, owner-scoped ──────────
    //
    // Six events, one distinct meaning each (the Werte track carries six for the same
    // reason: a stimulus and the answer to it are different facts with different
    // authors, and collapsing them loses who said what). All ride the ONE
    // `Agg::career::{userId}::Workshop::{workshopId}` stream with the deterministic
    // `writing_style` workshop id, and every one of them is ADDITIVE — the generic
    // `foldWorkshop` ignores all six, exactly as it ignores the Kartenspiel events.

    /** Round ① stimulus — the bullet set the person is asked to write out. AI-produced,
     *  supersede-by-latest (re-generating a brief the person has not answered yet is
     *  legitimate; the latest set is the one on screen). `run` is the compact telemetry
     *  of the real AI call (absent under the deterministic floor). */
    WorkshopWritingBulletsProducedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      bullets: "WritingBulletSet",
      "run?": "WorkshopStepRun | null",
    },

    /** Round ① answer — the person's own free text. This is the SANCTIONED open-text
     *  seam of the track (the one place the product asks for prose rather than a
     *  recognition), so it carries no grading and no interpretation: whatever they wrote
     *  is the evidence. Supersede-by-latest — rewriting your sample is not an error. */
    WorkshopWritingProseCapturedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      text: "NonEmptyString",
      occurredAt: "Timestamp",
    },

    /** Round ② stimulus SET — every scenario in one event, because the round is answered
     *  as one set (the question-SETS-per-round rule): a per-scenario event would let a
     *  half-generated round exist, and the fold would have to guess whether more is coming.
     *  AI-produced, supersede-by-latest. */
    WorkshopWritingScenariosProducedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      scenarios: "WritingScenario[]",
      "run?": "WorkshopStepRun | null",
    },

    /** Round ② answers as ONE batch — the verdicts for the whole scenario set, confirmed
     *  together. Batched for the same reason the stimuli are: a partial round is not a
     *  fact the person ever asserted. */
    WorkshopWritingScenariosAnsweredPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      verdicts: "WritingScenarioVerdict[]",
      occurredAt: "Timestamp",
    },

    /** Round ③ — a text written IN the style derived from rounds ① and ②, shown back to
     *  the person so they can correct the machine before anything durable is committed.
     *  AI-produced, supersede-by-latest; `context` names what the text was written for. */
    WorkshopWritingValidationProducedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      text: "NonEmptyString",
      "context?": "string",
      "run?": "WorkshopStepRun | null",
    },

    /** THE ASSET — emitted ONLY on the user's confirm (HITL, the same invariant as
     *  `workshop.WerteSektionProduced`): the derivation returns a transient draft, the
     *  person's edit wins, and only their confirm writes. Supersede-by-latest, and the
     *  profile's own `version` counts the confirmed derivations so a reader can see this
     *  is a living artifact rather than a one-shot. `run` is the compact telemetry of the
     *  derivation call; the authoritative Run stays on the Credits stream. */
    WorkshopWritingStyleProducedPayload: {
      "+": "reject",
      owner: "UserId",
      workshopId: "Urn",
      profile: "WritingStyleProfile",
      "run?": "WorkshopStepRun | null",
    },
  },

  resources: {
    workshop: {
      description: "A single workshop session (W1/W2/W3/W4)",
      type: "Workshop",
      urnPattern: "urn:career:workshop:{workshopId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/workshops/{workshopId}" },
      authz: { visibility: "user-owned", scopes: ["workshop:read"] },
    },
    candidateContext: {
      description: "The W1 CandidateContext for generation",
      type: "CandidateContext",
      urnPattern: "urn:career:context:{userId}:candidate",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/context/candidate" },
      authz: { visibility: "user-owned", scopes: ["workshop:read"] },
    },
    searchContext: {
      description: "The W2 SearchContext driving discovery",
      type: "SearchContext",
      urnPattern: "urn:career:context:{userId}:search",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/context/search" },
      authz: { visibility: "user-owned", scopes: ["workshop:read"] },
    },
    profilerRubric: {
      description: "The W3 ProfilerRubric driving matching",
      type: "ProfilerRubric",
      urnPattern: "urn:career:context:{userId}:profiler-rubric",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/context/profiler-rubric" },
      authz: { visibility: "user-owned", scopes: ["workshop:read"] },
    },
  },

  eventStream: {
    streamPattern: "Agg::career::{userId}::Workshop::{workshopId}",
    events: {
      "workshop.Started": {
        description: "A workshop session (W1/W2/W3) was started",
        type: "WorkshopStartedPayload",
      },
      "workshop.StimulusGenerated": {
        description: "The interviewer generated a recognition-card stimulus for a step (or skipped: stepSufficient)",
        type: "WorkshopStimulusGeneratedPayload",
      },
      "workshop.TurnRecorded": {
        description: "The user confirmed a typed answer for a card (one turn of the step)",
        type: "WorkshopTurnRecordedPayload",
      },
      "workshop.InterpretationProposed": {
        description: "The signal-fold produced a step's evidence digest at the AI boundary (auditable, not yet a claim)",
        type: "WorkshopInterpretationProposedPayload",
      },
      "workshop.ValueGameProgressed": {
        description: "Werte-Kartenspiel resume snapshot (optional, last-write-wins) — zero inference, device-portable resume",
        type: "WorkshopValueGameProgressedPayload",
      },
      "workshop.ValuePyramidFormed": {
        description: "Werte-Kartenspiel commit — the 1·3·4 value pyramid (the durable AI-free Phase-1 artifact); supersede-by-latest",
        type: "WorkshopValuePyramidFormedPayload",
      },
      "workshop.RoundPlanProduced": {
        description: "Runden-Planner produced a round plan from the latest pyramid + grounding (§4, V3); supersede-by-latest, auditable",
        type: "WorkshopRoundPlanProducedPayload",
      },
      "workshop.RoundPlanRevised": {
        description: "Runden-Planner revised the remaining rounds after a completed round (§4, V3.1); completed rounds preserved, supersede-by-latest",
        type: "WorkshopRoundPlanRevisedPayload",
      },
      "workshop.SchwartzProfilePrognosed": {
        description: "The interview prognosed a Schwartz circumplex profile from the pyramid + all answers (§5c, V4.2); additive, supersede-by-latest",
        type: "WorkshopSchwartzProfilePrognosedPayload",
      },
      "workshop.WerteSektionProduced": {
        description: "The HITL-confirmed personal Werte-Sektion — per-value detail + the four statements (§5b–§7, V5); supersede-by-latest, re-runnable",
        type: "WorkshopWerteSektionProducedPayload",
      },
      "workshop.WritingBulletsProduced": {
        description: "W4 round ① stimulus — the bullet set the person is asked to write out (AI); supersede-by-latest",
        type: "WorkshopWritingBulletsProducedPayload",
      },
      "workshop.WritingProseCaptured": {
        description: "W4 round ① answer — the person's own free text, the track's sanctioned open-text seam; supersede-by-latest",
        type: "WorkshopWritingProseCapturedPayload",
      },
      "workshop.WritingScenariosProduced": {
        description: "W4 round ② stimulus SET — invented scenarios × two same-content variants (AI); supersede-by-latest",
        type: "WorkshopWritingScenariosProducedPayload",
      },
      "workshop.WritingScenariosAnswered": {
        description: "W4 round ② answers — the whole scenario set's verdicts confirmed as ONE batch",
        type: "WorkshopWritingScenariosAnsweredPayload",
      },
      "workshop.WritingValidationProduced": {
        description: "W4 round ③ — a text written in the derived style, shown back for correction (AI); supersede-by-latest",
        type: "WorkshopWritingValidationProducedPayload",
      },
      "workshop.WritingStyleProduced": {
        description: "The W4 ASSET — the HITL-confirmed versioned WritingStyleProfile; supersede-by-latest, re-runnable",
        type: "WorkshopWritingStyleProducedPayload",
      },
      "workshop.StepAdvanced": {
        description: "A step's gate advanced; carries URN refs of claims produced by the step (canonical-first)",
        type: "WorkshopStepAdvancedPayload",
      },
      "workshop.SearchTargetingEdited": {
        description: "The user edited the produced SearchConfig targeting lists after consolidation (W2 editor)",
        type: "WorkshopSearchTargetingEditedPayload",
      },
      "workshop.ContextProduced": {
        description: "Consolidation produced the typed raw ContextSnapshot + the presented German narrative",
        type: "WorkshopContextProducedPayload",
      },
      "workshop.Completed": {
        description: "The workshop was completed and its context accepted — unlocks the successor",
        type: "WorkshopCompletedPayload",
      },
    },
  },

  authz: {
    scopes: {
      "workshop:read": { description: "Read the owner's workshops and produced contexts" },
      "workshop:write": { description: "Run workshops and produce contexts" },
    },
    policies: {
      "owner-only": {
        kind: "owner",
        description: "Workshops and contexts are owner-scoped to the calling Subject (law C1)",
      },
    },
    defaults: {
      unauthenticatedAccess: "deny",
      missingRolesBehavior: "deny",
    },
  },
} as const satisfies DomainDefinitionJSON

export default workshopDomain
